import React, { useRef, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import './Charts.css'

/**
 * BarChart Component
 *
 * A simple, responsive bar chart using CSS and optional animations.
 * No external charting library required.
 *
 * @param {Object} props
 * @param {Array} props.data - Chart data array
 * @param {string} props.labelKey - Key for labels in data
 * @param {string} props.valueKey - Key for values in data
 * @param {string} props.title - Chart title
 * @param {boolean} props.horizontal - Render horizontal bars
 * @param {boolean} props.showValues - Show values on bars
 * @param {boolean} props.showGrid - Show grid lines
 * @param {string} props.color - Primary bar color
 * @param {Function} props.formatValue - Value formatter function
 * @param {number} props.height - Chart height in pixels
 * @param {boolean} props.animate - Enable animations
 */
const BarChart = ({
	data = [],
	labelKey = 'label',
	valueKey = 'value',
	title,
	horizontal = false,
	showValues = true,
	showGrid = true,
	color,
	formatValue = (v) => v,
	height = 300,
	animate = true,
}) => {
	const chartRef = useRef(null)

	// Calculate max value for scaling
	const maxValue = useMemo(() => {
		if (!data.length) return 0
		return Math.max(...data.map((item) => item[valueKey] || 0))
	}, [data, valueKey])

	// Generate grid lines
	const gridLines = useMemo(() => {
		if (!showGrid || !maxValue) return []
		const lines = []
		const step = Math.ceil(maxValue / 5)
		for (let i = 0; i <= 5; i++) {
			lines.push(step * i)
		}
		return lines
	}, [maxValue, showGrid])

	// Calculate bar percentage
	const getBarPercentage = (value) => {
		if (!maxValue) return 0
		return Math.min((value / maxValue) * 100, 100)
	}

	// Get bar color
	const getBarColor = (item, index) => {
		if (item.color) return item.color
		if (color) return color
		// Default color palette
		const colors = [
			'var(--color-primary)',
			'var(--color-success)',
			'var(--color-info)',
			'var(--color-warning)',
			'var(--color-danger)',
		]
		return colors[index % colors.length]
	}

	if (!data.length) {
		return (
			<div className="chart chart--empty">
				<p>No data available</p>
			</div>
		)
	}

	return (
		<div className="chart bar-chart" ref={chartRef}>
			{title && <h4 className="chart__title">{title}</h4>}

			<div
				className={`bar-chart__container ${horizontal ? 'bar-chart__container--horizontal' : ''}`}
				style={{ height: horizontal ? 'auto' : height }}
			>
				{/* Grid Lines */}
				{showGrid && !horizontal && (
					<div className="bar-chart__grid">
						{gridLines.map((value, i) => (
							<div
								key={i}
								className="bar-chart__grid-line"
								style={{ bottom: `${(value / maxValue) * 100}%` }}
							>
								<span className="bar-chart__grid-label">{formatValue(value)}</span>
							</div>
						))}
					</div>
				)}

				{/* Bars */}
				<div className="bar-chart__bars">
					{data.map((item, index) => {
						const value = item[valueKey] || 0
						const percentage = getBarPercentage(value)
						const barColor = getBarColor(item, index)

						return (
							<div key={index} className="bar-chart__bar-group">
								<div
									className={`bar-chart__bar ${animate ? 'bar-chart__bar--animate' : ''}`}
									style={{
										[horizontal ? 'width' : 'height']: `${percentage}%`,
										backgroundColor: barColor,
										animationDelay: animate ? `${index * 50}ms` : '0ms',
									}}
									title={`${item[labelKey]}: ${formatValue(value)}`}
								>
									{showValues && percentage > 15 && (
										<span className="bar-chart__value">{formatValue(value)}</span>
									)}
								</div>
								<span className="bar-chart__label">{item[labelKey]}</span>
								{showValues && percentage <= 15 && (
									<span className="bar-chart__value-external">{formatValue(value)}</span>
								)}
							</div>
						)
					})}
				</div>
			</div>
		</div>
	)
}

BarChart.propTypes = {
	data: PropTypes.arrayOf(PropTypes.object).isRequired,
	labelKey: PropTypes.string,
	valueKey: PropTypes.string,
	title: PropTypes.string,
	horizontal: PropTypes.bool,
	showValues: PropTypes.bool,
	showGrid: PropTypes.bool,
	color: PropTypes.string,
	formatValue: PropTypes.func,
	height: PropTypes.number,
	animate: PropTypes.bool,
}

export default BarChart
